import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  TABS,
  PERIODS,
  LIVE_TABS,
  LIVE_CATEGORIES,
  buildLeaderboard,
  findCategory,
  defaultCategory,
  findLiveCategory,
  defaultLiveCategory,
  flagUrl,
} from "@/lib/rankingsData";
import useLiveRankings from "@/lib/useLiveRankings";
import {
  ChevronLeft,
  ChevronDown,
  Users,
  Globe2,
  Trophy,
  Search,
  Crown,
  Medal,
  Sparkles,
  MessageCircle,
  Radio,
  RefreshCw,
} from "lucide-react";
import DiscordLeaderboard from "@/components/DiscordLeaderboard";

const TAB_ICONS = {
  citizens: Users,
  nations:  Globe2,
  discord:  MessageCircle,
};

// Extra synthetic tab that isn't a mock-data leaderboard — sourced from Discord.
const DISCORD_TAB = { key: "discord", label: "Discord" };

// While the game engine only exposes citizens/countries, hide the org+alliance
// mock tabs from the public. Add their keys back to VISIBLE_TABS when the
// upstream endpoints ship — no other code changes needed.
const VISIBLE_TAB_KEYS = new Set(["citizens", "nations"]);
const VISIBLE_TABS = TABS.filter(t => VISIBLE_TAB_KEYS.has(t.key));

export default function Rankings() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("citizens");
  const isLive = LIVE_TABS.has(activeTab);
  const [activeCategory, setActiveCategory] = useState(() => defaultLiveCategory("citizens"));
  const [period, setPeriod] = useState("weekly");
  const [query, setQuery] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false); // collapsed by default on mobile

  const tab = TABS.find(t => t.key === activeTab);
  const category = isLive
    ? findLiveCategory(activeTab, activeCategory)
    : findCategory(activeTab, activeCategory);

  // ── Live data path ───────────────────────────────────────────
  const live = useLiveRankings({
    category: activeTab,
    metric:   category?.metric,
    limit:    25,
    enabled:  isLive && !!category?.metric,
  });

  // ── Mock data path (Orgs / Alliances) ────────────────────────
  const mockEntries = useMemo(
    () => (isLive ? [] : buildLeaderboard({ tab: activeTab, category: activeCategory, period, count: 25 })),
    [isLive, activeTab, activeCategory, period]
  );

  const entries = isLive ? live.entries : mockEntries;

  const filtered = useMemo(() => {
    if (!query.trim()) return entries;
    const q = query.trim().toLowerCase();
    return entries.filter(e =>
      (e.name || "").toLowerCase().includes(q) ||
      e.nation?.name?.toLowerCase().includes(q)
    );
  }, [entries, query]);

  const onTabChange = (tabKey) => {
    setActiveTab(tabKey);
    const nextCat = LIVE_TABS.has(tabKey)
      ? defaultLiveCategory(tabKey)
      : defaultCategory(tabKey);
    setActiveCategory(nextCat);
    setQuery("");
  };

  const top3 = entries.slice(0, 3);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12" data-testid="rankings-page">
      {/* Hero */}
      <div className="mb-7 md:mb-10">
        <Link to="/" className="inline-flex items-center gap-1 text-[10px] md:text-xs font-mono tracking-[0.2em] text-[var(--t-blue-glow)] uppercase hover:text-[var(--t-cyan)] mb-2" data-testid="rankings-back-home">
          <ChevronLeft className="w-3 h-3" /> {t("nav.home")}
        </Link>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <span className="font-mono text-[10px] md:text-xs tracking-[0.25em] md:tracking-[0.3em] text-[var(--t-blue-glow)] uppercase">{t("rankings.eyebrow")}</span>
            <h1 className="font-display text-3xl md:text-5xl mt-2 text-[var(--t-text)] uppercase leading-tight">{t("rankings.title")}</h1>
            <p className="text-sm text-[var(--t-text-dim)] mt-2 max-w-xl">
              {activeTab === "discord"
                ? "Territoria Discord community leaderboard, powered by the Discord Territoria Bot. Refreshed every 10 minutes."
                : t(`rankings.tabsDesc.${activeTab}`, { defaultValue: tab?.description })}
            </p>
          </div>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-[var(--t-border)] bg-[#050912] font-mono text-[10px] tracking-[0.2em] text-[var(--t-text-muted)] uppercase" data-testid="rankings-preview-tag">
            <Sparkles className="w-3 h-3" />
            {isLive
              ? "live · territoria.world"
              : (activeTab === "discord" ? "Discord Territoria Bot" : t("rankings.previewTag"))}
          </span>
        </div>
      </div>

      {/* Primary tabs */}
      <div className="flex flex-wrap gap-1.5 mb-6" data-testid="rankings-tabs">
        {[...VISIBLE_TABS, DISCORD_TAB].map(tb => {
          const Icon = TAB_ICONS[tb.key];
          const active = tb.key === activeTab;
          const isDiscord = tb.key === "discord";
          return (
            <button
              key={tb.key}
              onClick={() => (isDiscord ? setActiveTab("discord") : onTabChange(tb.key))}
              data-testid={`tab-${tb.key}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-[11px] md:text-xs font-mono tracking-[0.2em] uppercase border transition ${
                active
                  ? isDiscord
                    ? "border-[#5865F2] text-[#c5cbff] bg-[rgba(88,101,242,0.12)]"
                    : "border-[var(--t-blue)] text-[var(--t-cyan)] bg-[rgba(58,160,255,0.08)]"
                  : "border-[var(--t-border)] text-[var(--t-text-dim)] hover:text-[var(--t-text)] hover:border-[var(--t-border-hi)]"
              }`}
            >
              <Icon className="w-4 h-4" />
              {isDiscord ? tb.label : t(`rankings.tabs.${tb.key}`, { defaultValue: tb.label })}
            </button>
          );
        })}
      </div>

      {activeTab === "discord" ? (
        <DiscordLeaderboard />
      ) : (
      <>
      {/* Period filters — hidden on live tabs (no period concept upstream yet) */}
      {!isLive && (
      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="rankings-periods">
        <span className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-text-muted)] uppercase pr-1">{t("rankings.periodLabel")}</span>
        {PERIODS.map(p => {
          const active = p.key === period;
          return (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              data-testid={`period-${p.key}`}
              className={`px-3 py-1.5 text-[11px] md:text-xs font-mono tracking-[0.18em] uppercase border transition relative ${
                active
                  ? p.emphasized
                    ? "border-[var(--t-gold)] text-[var(--t-gold)] bg-[rgba(243,197,82,0.08)]"
                    : "border-[var(--t-blue)] text-[var(--t-cyan)] bg-[rgba(58,160,255,0.08)]"
                  : p.emphasized
                    ? "border-[rgba(243,197,82,0.3)] text-[var(--t-gold)] hover:bg-[rgba(243,197,82,0.05)]"
                    : "border-[var(--t-border)] text-[var(--t-text-dim)] hover:text-[var(--t-text)] hover:border-[var(--t-border-hi)]"
              }`}
            >
              {t(`rankings.periods.${p.key}`, { defaultValue: p.label })}
              {p.emphasized && !active && (
                <span className="ml-1.5 text-[9px] opacity-70">★</span>
              )}
            </button>
          );
        })}
      </div>
      )}

      {/* Live indicator — only on live tabs */}
      {isLive && (
        <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="live-indicator">
          <span
            className={`inline-flex items-center gap-2 px-3 py-1.5 border font-mono text-[10px] tracking-[0.25em] uppercase ${
              live.error
                ? "border-[var(--t-red)] text-[var(--t-red)] bg-[rgba(220,80,80,0.06)]"
                : live.stale
                ? "border-[#f3c552] text-[#f3c552] bg-[rgba(243,197,82,0.06)]"
                : "border-[#5fd99a] text-[#5fd99a] bg-[rgba(95,217,154,0.06)]"
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${live.loading ? "animate-pulse" : ""}`}
              style={{
                background: live.error ? "var(--t-red)" : live.stale ? "#f3c552" : "#5fd99a",
                boxShadow:  live.error ? "0 0 8px var(--t-red)" : live.stale ? "0 0 8px #f3c552" : "0 0 8px #5fd99a",
              }}
            />
            <Radio className="w-3 h-3" />
            {live.error ? "reconnecting…" : live.stale ? "cached snapshot" : "live · from territoria.world"}
          </span>
          {live.updatedAt && (
            <span className="font-mono text-[10px] tracking-[0.2em] text-[var(--t-text-muted)] uppercase">
              // updated {formatRelative(live.updatedAt)}
            </span>
          )}
          <button
            type="button"
            onClick={() => live.refresh()}
            disabled={live.loading}
            className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-mono tracking-[0.2em] uppercase border border-[var(--t-border)] text-[var(--t-text-dim)] hover:text-[var(--t-text)] hover:border-[var(--t-border-hi)] transition"
            data-testid="live-refresh"
          >
            <RefreshCw className={`w-3 h-3 ${live.loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      )}

      {/* Body grid: sidebar + main */}
      <div className="grid md:grid-cols-[240px_1fr] gap-5 md:gap-7">
        {/* ---- Category sidebar ---- */}
        <aside data-testid="rankings-sidebar">
          <div className="panel md:sticky md:top-20 md:max-h-[calc(100vh-6rem)] md:overflow-auto">
            {/* Mobile collapse trigger */}
            <button
              onClick={() => setSidebarOpen(o => !o)}
              className="md:hidden w-full flex items-center justify-between gap-2 px-4 py-3 text-left border-b border-[var(--t-border)]"
              data-testid="sidebar-toggle"
              aria-expanded={sidebarOpen}
            >
              <span className="flex items-center gap-2 min-w-0">
                <Trophy className="w-3.5 h-3.5 text-[var(--t-blue-glow)] flex-shrink-0" />
                <span className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase">{t("rankings.categoryHeader")}</span>
                <span className="font-display text-[11px] tracking-[0.05em] uppercase text-[var(--t-cyan)] truncate">
                  · {t(`rankings.cats.${activeCategory}`, { defaultValue: category.label })}
                </span>
              </span>
              <ChevronDown className={`w-4 h-4 text-[var(--t-blue-glow)] flex-shrink-0 transition-transform ${sidebarOpen ? "rotate-180" : ""}`} />
            </button>

            {/* Sidebar content — hidden on mobile unless toggled open */}
            <div className={`${sidebarOpen ? "block" : "hidden"} md:block p-3`}>
              <div className="hidden md:flex items-center gap-2 mb-3 px-1">
                <Trophy className="w-3.5 h-3.5 text-[var(--t-blue-glow)]" />
                <span className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase">{t("rankings.categoriesHeader")}</span>
              </div>
              <div className="space-y-4">
                {(isLive ? LIVE_CATEGORIES[activeTab] : tab.groups).map(g => (
                  <div key={g.group}>
                    <div className="px-2 mb-1 font-mono text-[9px] tracking-[0.25em] uppercase text-[var(--t-text-muted)]">
                      // {t(`rankings.groups.${g.group}`, { defaultValue: g.group })}
                    </div>
                    <ul className="space-y-0.5">
                      {g.items.map(it => {
                        const active = it.key === activeCategory;
                        return (
                          <li key={it.key}>
                            <button
                              onClick={() => { setActiveCategory(it.key); setSidebarOpen(false); }}
                              data-testid={`cat-${it.key}`}
                              className={`w-full text-left px-3 py-2 text-[11px] font-display tracking-[0.05em] uppercase border-l-2 transition flex items-center justify-between gap-2 ${
                                active
                                  ? "border-[var(--t-blue)] text-[var(--t-cyan)] bg-[rgba(58,160,255,0.08)]"
                                  : "border-transparent text-[var(--t-text-dim)] hover:text-[var(--t-text)] hover:bg-[rgba(58,160,255,0.04)]"
                              }`}
                            >
                              <span>{t(`rankings.cats.${it.key}`, { defaultValue: it.label })}</span>
                              {it.emphasized && (
                                <span className="text-[8px] tracking-normal text-[var(--t-gold)]">★</span>
                              )}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                </div>
              ))}
              </div>
            </div>
          </div>
        </aside>

        {/* ---- Main content ---- */}
        <main className="min-w-0 space-y-5">
          {/* Category header + search */}
          <div className="panel p-4 md:p-5 flex flex-wrap items-center justify-between gap-3" data-testid="rankings-header">
            <div className="min-w-0">
              <span className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase">// {t(`rankings.tabs.${activeTab}`, { defaultValue: tab?.label || activeTab })}{!isLive ? ` · ${t(`rankings.periods.${period}`, { defaultValue: PERIODS.find(p => p.key === period).label })}` : " · Live"}</span>
              <h2 className="font-display text-xl md:text-2xl text-[var(--t-text)] uppercase mt-1 truncate">{t(`rankings.cats.${activeCategory}`, { defaultValue: category?.label || activeCategory })}</h2>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--t-text-muted)] pointer-events-none" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("rankings.searchPlaceholder")}
                  data-testid="rankings-search"
                  className="w-44 md:w-56 pl-8 pr-3 py-2 text-xs font-mono bg-[#050912] border border-[var(--t-border)] focus:border-[var(--t-blue)] outline-none text-[var(--t-text)] placeholder:text-[var(--t-text-muted)] transition"
                />
              </div>
            </div>
          </div>

          {/* Live-loading skeleton */}
          {isLive && live.loading && entries.length === 0 && (
            <div className="panel overflow-hidden" data-testid="rankings-loading">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-3 md:px-5 py-3 border-b border-[var(--t-border)] last:border-0 animate-pulse">
                  <div className="w-9 h-4 bg-[var(--t-panel-2)]" />
                  <div className="w-8 h-8 rounded-full bg-[var(--t-panel-2)]" />
                  <div className="flex-1 h-4 bg-[var(--t-panel-2)] max-w-[200px]" />
                  <div className="w-16 h-4 bg-[var(--t-panel-2)]" />
                </div>
              ))}
            </div>
          )}

          {/* Podium (top 3) — only if not searching or all match */}
          {!query.trim() && top3.length >= 3 && (
            <Podium entries={top3} category={category} />
          )}

          {/* Leaderboard table */}
          <Leaderboard entries={filtered} category={category} entity={tab?.entity || activeTab.replace(/s$/, "")} t={t} />
        </main>
      </div>
      </>
      )}
    </div>
  );
}

// ============================================================
// PODIUM — Gold / Silver / Bronze
// ============================================================

const PODIUM_META = {
  1: { tier: "gold",   color: "#f3c552", glow: "rgba(243,197,82,0.55)", icon: Crown, label: "1st" },
  2: { tier: "silver", color: "#d6deec", glow: "rgba(214,222,236,0.45)", icon: Medal, label: "2nd" },
  3: { tier: "bronze", color: "#cd7f32", glow: "rgba(205,127,50,0.55)", icon: Medal, label: "3rd" },
};

function Podium({ entries, category }) {
  // Reorder for visual: silver, gold, bronze (so gold sits centered & taller)
  const [first, second, third] = entries;
  return (
    <div className="panel p-5 md:p-6" data-testid="rankings-podium">
      <div className="flex items-end justify-between gap-3 md:gap-5">
        <PodiumSlot entry={second} category={category} size="md" />
        <PodiumSlot entry={first}  category={category} size="lg" />
        <PodiumSlot entry={third}  category={category} size="md" />
      </div>
    </div>
  );
}

function PodiumSlot({ entry, category, size }) {
  const { t } = useTranslation();
  if (!entry) return <div className="flex-1" />;
  const meta = PODIUM_META[entry.rank];
  const Icon = meta.icon;
  const isLarge = size === "lg";
  const flagSrc = entry.nation ? flagUrl(entry.nation.code) : null;
  return (
    <div className="flex-1 flex flex-col items-center text-center" data-testid={`podium-${entry.rank}`}>
      <Icon
        className={`${isLarge ? "w-7 h-7 md:w-8 md:h-8" : "w-5 h-5 md:w-6 md:h-6"} mb-2`}
        style={{ color: meta.color, filter: `drop-shadow(0 0 10px ${meta.glow})` }}
        strokeWidth={1.4}
      />
      <div
        className={`relative ${isLarge ? "w-20 h-20 md:w-24 md:h-24" : "w-14 h-14 md:w-16 md:h-16"} flex items-center justify-center border bg-[#050912] mb-3`}
        style={{
          borderColor: meta.color,
          boxShadow: `inset 0 0 24px ${meta.glow}, 0 0 24px ${meta.glow}`,
        }}
      >
        {flagSrc ? (
          <img src={flagSrc} alt={entry.nation.name} className={`${isLarge ? "w-12 h-8 md:w-14 md:h-10" : "w-9 h-6 md:w-10 md:h-7"} object-cover border border-[var(--t-border)]`} />
        ) : entry.nation?.emoji ? (
          <span className={`${isLarge ? "text-4xl md:text-5xl" : "text-2xl md:text-3xl"}`} aria-label={entry.nation.name}>
            {entry.nation.emoji}
          </span>
        ) : (
          <span className={`font-display ${isLarge ? "text-3xl md:text-4xl" : "text-xl md:text-2xl"}`} style={{ color: meta.color }}>
            {entry.rank}
          </span>
        )}
        <span
          className={`absolute -top-2 -right-2 inline-flex items-center justify-center ${isLarge ? "w-7 h-7 text-xs" : "w-6 h-6 text-[10px]"} font-display border bg-[#050912]`}
          style={{ borderColor: meta.color, color: meta.color, boxShadow: `0 0 10px ${meta.glow}` }}
        >
          {entry.rank}
        </span>
      </div>
      <div
        className={`font-display ${isLarge ? "text-lg md:text-xl" : "text-sm md:text-base"} uppercase leading-tight truncate max-w-full px-1`}
        style={{ color: meta.color, textShadow: `0 0 14px ${meta.glow}` }}
      >
        {entry.name}
      </div>
      {entry.nation && entry.nation.name !== entry.name && (
        <div className="text-[10px] text-[var(--t-text-dim)] mt-0.5 truncate max-w-full">
          {entry.nation.name}
        </div>
      )}
      <div className={`mt-2 font-mono ${isLarge ? "text-sm md:text-base" : "text-xs md:text-sm"} text-[var(--t-text)]`}>
        {category.fmt(entry.score)}
      </div>
      <div className="mt-1 font-mono text-[9px] tracking-[0.25em] uppercase" style={{ color: meta.color }}>
        {t(`rankings.podium.${meta.tier}`, { defaultValue: meta.tier })}
      </div>
    </div>
  );
}

// ============================================================
// LEADERBOARD
// ============================================================

function Leaderboard({ entries, category, entity, t }) {
  if (entries.length === 0) {
    return (
      <div className="panel p-10 text-center" data-testid="rankings-empty">
        <Trophy className="w-12 h-12 text-[var(--t-text-muted)] mx-auto mb-3" />
        <h3 className="font-display text-lg text-[var(--t-cyan)] uppercase mb-2">{t("rankings.noMatchesTitle")}</h3>
        <p className="text-sm text-[var(--t-text-dim)]">{t("rankings.noMatchesBlurb", { entity: t(`rankings.entity.${entity}`, { defaultValue: entity }) })}</p>
      </div>
    );
  }
  return (
    <div className="panel overflow-hidden" data-testid="rankings-leaderboard">
      {/* Header row — hidden on mobile (too cramped) */}
      <div className="hidden md:grid grid-cols-[80px_1fr_200px] gap-4 px-5 py-3 border-b border-[var(--t-border)] bg-gradient-to-r from-[rgba(58,160,255,0.04)] to-transparent font-mono text-[10px] tracking-[0.25em] text-[var(--t-text-muted)] uppercase">
        <span>{t("rankings.rank")}</span>
        <span>{entity === "nation" ? t("rankings.nationCol") : t("rankings.nameCol")}</span>
        <span className="text-right">{category.unit || t("rankings.scoreCol")}</span>
      </div>
      <ul>
        {entries.map(e => (
          <LeaderboardRow key={`${e.rank}-${e.name}`} entry={e} category={category} t={t} />
        ))}
      </ul>
    </div>
  );
}

function LeaderboardRow({ entry, category, t }) {
  const topThree = entry.rank <= 3;
  const tierColor = topThree ? PODIUM_META[entry.rank].color : "var(--t-text)";
  const flagSrc = entry.nation ? flagUrl(entry.nation.code) : null;

  return (
    <li
      data-testid={`leaderboard-row-${entry.rank}`}
      className={`flex items-center gap-3 md:grid md:grid-cols-[80px_1fr_200px] md:gap-4 px-3 md:px-5 py-3 border-b border-[var(--t-border)] last:border-0 hover:bg-[rgba(58,160,255,0.04)] transition ${
        topThree ? "bg-[rgba(58,160,255,0.02)]" : ""
      }`}
    >
      {/* Rank */}
      <div className="font-display text-base md:text-lg leading-none flex-shrink-0 w-9 md:w-auto" style={{ color: tierColor }}>
        #{entry.rank}
      </div>

      {/* Name + flag */}
      <div className="flex items-center gap-2.5 md:gap-3 min-w-0 flex-1">
        {flagSrc ? (
          <img
            src={flagSrc}
            alt={entry.nation.name}
            className="w-7 h-5 md:w-8 md:h-5 object-cover border border-[var(--t-border)] flex-shrink-0"
            loading="lazy"
          />
        ) : entry.nation?.emoji ? (
          <span className="text-lg md:text-xl leading-none flex-shrink-0" aria-label={entry.nation.name}>
            {entry.nation.emoji}
          </span>
        ) : null}
        <div className="min-w-0 flex-1">
          <div
            className="font-display text-sm md:text-base uppercase truncate leading-tight"
            style={topThree ? { color: tierColor, textShadow: `0 0 10px ${PODIUM_META[entry.rank].glow}` } : undefined}
          >
            {entry.name}
          </div>
          {entry.nation && entry.nation.name !== entry.name && (
            <div className="font-mono text-[9px] md:text-[10px] tracking-[0.15em] uppercase text-[var(--t-text-muted)] truncate">
              {entry.nation.name}
            </div>
          )}
        </div>
      </div>

      {/* Score — mobile: right-aligned; desktop: dedicated column */}
      <div className="md:hidden flex-shrink-0 font-mono text-xs text-[var(--t-text)] whitespace-nowrap">
        {category.fmt(entry.score)}
      </div>
      <div className="hidden md:block font-mono text-sm text-right text-[var(--t-text)] truncate">
        {category.fmt(entry.score)}
      </div>
    </li>
  );
}

function formatRelative(iso) {
  if (!iso) return "";
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return "";
  const diff = Math.max(0, (Date.now() - ts) / 1000);
  if (diff < 60)   return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

